import { LoadingIndicator } from '@components/atoms/LoadingIndicator/LoadingIndicator';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Title } from '@components/atoms/Title';
import { TvShowtime } from '@components/molecules/TvShowtime/TvShowtime';
import { Default } from '@components/templates/layouts/Default';
import { extractSsrState } from '@lib/graphql/helpers/extractSsrState';
import { createUrqlClient, useQuery } from '@lib/graphql/urql';
import { handleRedirect } from '@lib/redirects/redirect';
import { GetBySlugFullDocument, type TvmTopicEntry } from '@src/lib/graphql/generated';
import { decodeOptions } from '@src/utils/urlHelpers';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';

interface TvmTopicEntryPageProps {
  slug: string;
}

export const TvmTopicEntryPage = ({ slug }: TvmTopicEntryPageProps) => {
  const [{ data, fetching: loading }] = useQuery({
    query: GetBySlugFullDocument,
    variables: { slug, portal: 'tvmedia' },
    requestPolicy: 'cache-and-network',
  });

  const entry: TvmTopicEntry =
    data?.findBySlug?.__typename === 'TvmTopicEntry' ? (data?.findBySlug as TvmTopicEntry) : null;

  if (!entry) {
    return null;
  }

  // Fall back to the topic's cover image, then the first showtime that has an image, as og/twitter image when no explicit one is set.
  const metadata = {
    ...entry.metadata,
    open_graph_image:
      entry.metadata?.open_graph_image ??
      entry.cover_image ??
      entry.topic?.showtimes?.find((showtime) => showtime?.image?.url)?.image,
  };

  return (
    <div>
      <Head>{Meta(metadata)}</Head>
      <Title level={1}>{entry.title}</Title>
      <div
        className="prose prose-streaming mt-4 lg:max-w-screen-md"
        dangerouslySetInnerHTML={{ __html: entry.text ?? '' }}
      ></div>
      {loading ? (
        <LoadingIndicator visible />
      ) : (
        <section className="mt-16 grid grid-flow-row-dense grid-cols-fill-streaming-default gap-x-4 gap-y-16">
          {entry.topic?.showtimes?.map((showtime) => (
            <TvShowtime key={showtime.id} showtime={showtime} showDate showChannelName />
          ))}
        </section>
      )}
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { ssrOptionalAuthQuery, ssrCache } = createUrqlClient();

  const options = decodeOptions(params?.slug);

  const { data: findBySlugData } = await ssrOptionalAuthQuery({
    query: GetBySlugFullDocument,
    variables: {
      portal: 'tvmedia',
      slug: `/guide/${options.slug}`,
    },
    authToken: options?.params?.token,
  });

  const topic = findBySlugData?.findBySlug;

  if (!topic) return { notFound: true };

  // Handle redirect result
  if ('metadata' in topic && topic?.metadata?.redirect) {
    return handleRedirect(301, topic?.metadata?.redirect);
  }

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      slug: `/guide/${options.slug}`,
      urqlState: extractSsrState(ssrCache),
    },
  };
};

TvmTopicEntryPage.PageLayout = Default;

export default TvmTopicEntryPage;
